/**
 * The organisation's directory, for picking people and groups by name
 * instead of typing object ids. On Entra it is Microsoft Graph, called from
 * the browser with the person's own token (User.ReadBasic.All and
 * GroupMember.Read.All, consented for the tenant). No SDK: two fetches.
 * When that token cannot be had (consent not granted, a development
 * sign-in) or Graph refuses, the API answers instead: `GET
 * /people/v1/directory` and `/people/v1/groups` to search, `POST
 * /people/v1/subjects` to name ids (schemas/directorySubjects.ts), asked of
 * Graph as the AIOE API application. Nobody types or pastes an object id.
 *
 * Mojo Up AI Cloud has no Graph and no directory groups: its directory is
 * the active organisation's member list (`/cloud/v1/organisations/:id/members`),
 * people only, and `groupsAvailable()` is false so no screen offers a group.
 */

import type { DirectoryGroupsResponse, DirectorySubjectsResponse, PeopleDirectoryResponse } from '@aioe/shared';

import { api } from './api/client.ts';
import { isCloud } from './edition.ts';

export type SubjectKind = 'user' | 'group';
export interface Subject {
  type: SubjectKind;
  /** The object id, which is what the platform stores. */
  id: string;
  name: string;
  /** Email, principal name or a group's description. */
  detail?: string;
  /** The directory was asked and does not know this id (a person who has left, a deleted group). */
  missing?: boolean;
}

export const GRAPH_SCOPES = ['https://graph.microsoft.com/User.ReadBasic.All', 'https://graph.microsoft.com/GroupMember.Read.All'];
const GRAPH = 'https://graph.microsoft.com/v1.0';

let graphToken: (() => Promise<string | undefined>) | undefined;
export function setGraphTokenProvider(p: (() => Promise<string | undefined>) | undefined): void {
  graphToken = p;
}

type Member = { userId: string; name?: string; email?: string };
let members: (() => Promise<Member[]>) | undefined;
/** Mojo Up AI Cloud: the active organisation's members, loaded once per organisation. */
export function setMemberDirectory(load: (() => Promise<Member[]>) | undefined): void {
  members = load;
}
async function memberSubjects(): Promise<Subject[]> {
  const list = ((await members?.()) ?? []).map((m): Subject => ({ type: 'user', id: m.userId, name: m.name ?? m.email ?? m.userId, ...(m.email ? { detail: m.email } : {}) }));
  remember(list);
  return list;
}

/** Whether there is a directory to search: Graph, Cloud's members, or (on Enterprise) the API's own. */
export const hasDirectory = () => Boolean(graphToken || members || !isCloud());

/** Whether this organisation has directory groups to offer at all: never on Mojo Up AI Cloud. */
export const groupsAvailable = (): boolean => !isCloud() && !members;

/** False once a search learned that groups cannot be searched here (no Graph for the person, nor for the API). */
let groupSearch = true;
export const groupsSearchable = (): boolean => groupsAvailable() && groupSearch;

async function graph<T>(path: string, init: RequestInit = {}): Promise<T | undefined> {
  const token = await graphToken?.();
  if (!token) return undefined;
  const res = await fetch(`${GRAPH}${path}`, { ...init, headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json', ConsistencyLevel: 'eventual', ...(init.headers as Record<string, string>) } });
  if (!res.ok) throw new Error(`directory: ${res.status}`);
  return (await res.json()) as T;
}
/** Graph with the person's token, or undefined when it cannot be asked or refuses: the API is asked instead. */
const graphOrNot = <T,>(path: string, init?: RequestInit) => graph<T>(path, init).catch(() => undefined);

/**
 * The signed-in person's own profile picture from Microsoft Graph, as an object URL for an <img>.
 * A person with no photo (404), or a token that may not read it (401, 403), simply has none: never an error.
 */
export async function photoOf(token: string | undefined): Promise<string | undefined> {
  if (!token) return undefined;
  try {
    const res = await fetch(`${GRAPH}/me/photo/$value`, { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return blob.size ? URL.createObjectURL(blob) : undefined;
  } catch {
    return undefined;
  }
}

const quote = (q: string) => q.replace(/["\\]/g, ' ').trim();

/** Up to eight people and eight groups whose name or email starts with the words typed. */
export async function searchDirectory(query: string, kinds: SubjectKind[]): Promise<Subject[]> {
  const q = quote(query);
  if (q.length < 2) return [];
  if (members) {
    if (!kinds.includes('user')) return [];
    const needle = q.toLowerCase();
    return (await memberSubjects()).filter((s) => s.name.toLowerCase().includes(needle) || s.detail?.toLowerCase().includes(needle)).slice(0, 8);
  }
  const wantGroups = kinds.includes('group') && groupsAvailable();
  const out: Subject[] = [];
  const [users, groups] = await Promise.all([
    kinds.includes('user')
      ? graphOrNot<{ value: Array<{ id: string; displayName?: string; mail?: string; userPrincipalName?: string }> }>(`/users?$search="displayName:${q}" OR "mail:${q}" OR "userPrincipalName:${q}"&$select=id,displayName,mail,userPrincipalName&$top=8&$orderby=displayName`).then(
          async (g) => g?.value.map((u): Subject => ({ type: 'user', id: u.id, name: u.displayName ?? u.userPrincipalName ?? u.id, ...((u.mail ?? u.userPrincipalName) ? { detail: u.mail ?? u.userPrincipalName } : {}) })) ?? (await api<PeopleDirectoryResponse>(`/people/v1/directory?q=${encodeURIComponent(q)}&limit=8`)).people.map((p): Subject => ({ type: 'user', id: p.id, name: p.name, ...(p.email ? { detail: p.email } : {}) })),
        )
      : undefined,
    wantGroups
      ? graphOrNot<{ value: Array<{ id: string; displayName?: string; mail?: string; description?: string }> }>(`/groups?$search="displayName:${q}"&$select=id,displayName,mail,description&$top=8&$orderby=displayName`).then(async (g) => {
          if (g) {
            groupSearch = true;
            return g.value.map((x): Subject => ({ type: 'group', id: x.id, name: x.displayName ?? 'A group with no name', detail: x.mail ?? x.description ?? 'Group' }));
          }
          const a = await api<DirectoryGroupsResponse>(`/people/v1/groups?q=${encodeURIComponent(q)}&limit=8`);
          groupSearch = a.available;
          return a.groups.map((x): Subject => ({ type: 'group', id: x.id, name: x.name, detail: x.detail ?? 'Group' }));
        })
      : undefined,
  ]);
  out.push(...(users ?? []), ...(groups ?? []));
  remember(out);
  return out;
}

const names = new Map<string, Subject>();
const listeners = new Set<() => void>();
function remember(subjects: Subject[]) {
  for (const s of subjects) names.set(s.id, s);
  for (const l of listeners) l();
}

/** Names for ids the page shows (members, grants); looked up once and kept for the session. */
export async function resolveSubjects(ids: string[]): Promise<void> {
  if (members) {
    if (ids.some((id) => !names.has(id))) await memberSubjects();
    for (const id of ids) if (!names.has(id)) names.set(id, { type: 'user', id, name: id, missing: true });
    for (const l of listeners) l();
    return;
  }
  const missing = [...new Set(ids)].filter((id) => !names.has(id));
  if (missing.length === 0) return;
  for (const id of missing) names.set(id, { type: 'user', id, name: id }); // one lookup per id, whatever the answer
  const objectIds = missing.filter((id) => /^[0-9a-f-]{36}$/i.test(id)).slice(0, 1000);
  const res = objectIds.length
    ? await graphOrNot<{ value: Array<{ '@odata.type': string; id: string; displayName?: string; mail?: string; userPrincipalName?: string }> }>(`/directoryObjects/getByIds`, { method: 'POST', body: JSON.stringify({ ids: objectIds, types: ['user', 'group'] }) })
    : undefined;
  const found: Subject[] = (res?.value ?? []).map((o): Subject => ({ type: o['@odata.type'].endsWith('group') ? 'group' : 'user', id: o.id, name: o.displayName ?? o.userPrincipalName ?? o.id, ...((o.mail ?? o.userPrincipalName) ? { detail: o.mail ?? o.userPrincipalName } : {}) }));
  // What Graph did not name (or could not be asked about) the API names: people from their sign-ins, people and groups from Entra.
  const rest = missing.filter((id) => !found.some((s) => s.id === id));
  const gone: string[] = [];
  for (let i = 0; i < rest.length; i += 200) {
    const a = await api<DirectorySubjectsResponse>('/people/v1/subjects', { method: 'POST', json: { ids: rest.slice(i, i + 200) } }).catch(() => undefined);
    if (!a) continue;
    found.push(...a.subjects.map((s): Subject => ({ type: s.type, id: s.id, name: s.name, ...(s.detail ? { detail: s.detail } : {}) })));
    gone.push(...a.missing);
  }
  // Only a directory id nobody knows is someone who left; any other id keeps showing as it is.
  remember([...found, ...gone.filter((id) => /^(usr_)?[0-9a-f-]{36}$/i.test(id)).map((id): Subject => ({ type: 'user', id, name: id, missing: true }))]);
}

export const subjectName = (id: string): Subject | undefined => names.get(id);
export function subscribeNames(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
